import { nonEmptyArray } from 'fp-ts';
import { useMemo } from 'react';
import { Product, Service, ServiceType } from '@mews-commander/data-access';
import { useServicesQuery } from '@mews-commander/legacy-entities/src/queries/services/servicesQuery';
import { useProductsByService } from './useProductsByService';

const groupServiceByType = nonEmptyArray.groupBy<Service>((s) => s.data.discriminator);

const getServiceIds = (services: Service[] | undefined) => services?.map(({ id }) => id);

export const useProductsByServiceType = () => {
    const servicesQuery = useServicesQuery();

    const services = servicesQuery.data?.services;
    const servicesByType = useMemo(() => (services ? groupServiceByType(services) : {}), [services]);

    const reservableProductsByService = useProductsByService(getServiceIds(servicesByType[ServiceType.Bookable]));
    const orderableProductsByService = useProductsByService(getServiceIds(servicesByType[ServiceType.Additional]));

    return useMemo(
        () => ({
            productsByServiceType: {
                [ServiceType.Bookable]: getServicesAndItsProducts(
                    servicesByType[ServiceType.Bookable],
                    reservableProductsByService.productsByService
                ),
                [ServiceType.Additional]: getServicesAndItsProducts(
                    servicesByType[ServiceType.Additional],
                    orderableProductsByService.productsByService
                ),
            },
            productCategories: [
                ...(reservableProductsByService.productCategories ?? []),
                ...(orderableProductsByService.productCategories ?? []),
            ],
            loading:
                servicesQuery.isFetching || reservableProductsByService.loading || orderableProductsByService.loading,
        }),
        [servicesByType, reservableProductsByService, orderableProductsByService, servicesQuery.isFetching]
    );
};

const getServicesAndItsProducts = (
    services: Service[] | undefined,
    productsByService: Record<string, Product[] | undefined>
) =>
    services
        ? services.map((service) => ({
              service,
              products: productsByService[service.id]?.filter(({ isDefault }) => !isDefault) ?? [],
          }))
        : [];
